import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FilterList, Close } from '@mui/icons-material';
import { en } from '../translations/en';
import { useGettyVocabulary } from '../hooks/useGettyVocabulary';

interface AdvancedFilterProps {
  onFilter: (filters: FilterCriteria) => void;
  loading?: boolean;
  initialFilters?: FilterCriteria;
}

export interface FilterCriteria {
  artworkTitle?: string;
  provenance?: string;
  dateFrom?: string;
  dateTo?: string;
  heightFrom?: string;
  heightTo?: string;
  widthFrom?: string;
  widthTo?: string;
  dimensionUnit?: 'in' | 'cm';
  keywords?: string;
  workType?: string;
  materials?: string[];
  techniques?: string[];
  creators?: string;
}

const INITIAL_FILTERS: FilterCriteria = {
  artworkTitle: '',
  provenance: '',
  dateFrom: '',
  dateTo: '',
  heightFrom: '',
  heightTo: '',
  widthFrom: '',
  widthTo: '',
  dimensionUnit: 'in',
  keywords: '',
  workType: '',
  materials: [],
  techniques: [],
  creators: '',
};

const filterOptions = createFilterOptions<string>({ limit: 100 });

/**
 * Sanitize a string value to a non-negative number string.
 * Returns '' for empty input. Strips any value that parses to < 0.
 */
function sanitizeNonNegative(value: string): string {
  if (value === '') return '';
  // Reject if the raw string starts with '-' or parses negative
  if (value.startsWith('-')) return '';
  const num = Number(value);
  if (!isNaN(num) && num < 0) return '';
  return value;
}

interface RangeErrors {
  height: string | null;
  width: string | null;
  year: string | null;
}

function computeRangeErrors(filters: FilterCriteria): RangeErrors {
  const isRangeInvalid = (from: string | undefined, to: string | undefined): boolean => {
    if (!from || !to) return false;
    return Number(from) > Number(to);
  };

  return {
    height: isRangeInvalid(filters.heightFrom, filters.heightTo)
      ? en.advancedFilter.rangeErrorHeightFromTo
      : null,
    width: isRangeInvalid(filters.widthFrom, filters.widthTo)
      ? en.advancedFilter.rangeErrorWidthFromTo
      : null,
    year: isRangeInvalid(filters.dateFrom, filters.dateTo)
      ? en.advancedFilter.rangeErrorYearFromTo
      : null,
  };
}

export const AdvancedFilter = ({ onFilter, loading = false, initialFilters }: AdvancedFilterProps) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterCriteria>({ ...INITIAL_FILTERS });
  const { workTypes, materials, techniques, loading: vocabLoading, error: vocabError } = useGettyVocabulary();

  const rangeErrors = computeRangeErrors(filters);
  const hasRangeError = rangeErrors.height !== null || rangeErrors.width !== null || rangeErrors.year !== null;

  const handleOpen = () => {
    // Sync internal filter state from parent when opening (e.g., after loading a saved search)
    if (initialFilters) {
      setFilters({ ...INITIAL_FILTERS, ...initialFilters });
    }
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleApply = () => {
    if (hasRangeError) return;
    onFilter(filters);
    handleClose();
  };

  const handleClear = () => {
    const clearedFilters: FilterCriteria = { ...INITIAL_FILTERS };
    setFilters(clearedFilters);
    onFilter(clearedFilters);
  };

  const numericInputProps = { min: 0, inputMode: 'numeric' as const };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterList />}
        onClick={handleOpen}
        sx={{
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          py: 1.5,
          borderColor: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light + '10'
          }
        }}
      >
        {en.advancedFilter.filter}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          {en.advancedFilter.title}
          <Button
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </Button>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.basicInfo}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={en.advancedFilter.artworkTitle}
                  value={filters.artworkTitle}
                  onChange={(e) => setFilters({ ...filters, artworkTitle: e.target.value })}
                  placeholder={en.advancedFilter.artworkTitlePlaceholder}
                  fullWidth
                />
                <TextField
                  label={en.advancedFilter.keywords}
                  value={filters.keywords}
                  onChange={(e) => setFilters({ ...filters, keywords: e.target.value })}
                  placeholder={en.advancedFilter.keywordsPlaceholder}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.classification}
              </Typography>
              {vocabError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Getty vocabulary data unavailable. You can still search by other criteria.
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Autocomplete
                  freeSolo
                  options={workTypes}
                  value={filters.workType || null}
                  onChange={(_e, value) => setFilters({ ...filters, workType: (value as string) || '' })}
                  onInputChange={(_e, value) => setFilters({ ...filters, workType: value })}
                  filterOptions={filterOptions}
                  loading={vocabLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={en.advancedFilter.workType}
                      placeholder={en.advancedFilter.workTypePlaceholder}
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={techniques}
                  value={filters.techniques || []}
                  onChange={(_e, value) => setFilters({ ...filters, techniques: value as string[] })}
                  filterOptions={filterOptions}
                  loading={vocabLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={en.advancedFilter.techniques}
                      placeholder={en.advancedFilter.techniquesPlaceholder}
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={materials}
                  value={filters.materials || []}
                  onChange={(_e, value) => setFilters({ ...filters, materials: value as string[] })}
                  filterOptions={filterOptions}
                  loading={vocabLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={en.advancedFilter.materials}
                      placeholder={en.advancedFilter.materialsPlaceholder}
                    />
                  )}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.creator}
              </Typography>
              <TextField
                label={en.advancedFilter.creator}
                value={filters.creators}
                onChange={(e) => setFilters({ ...filters, creators: e.target.value })}
                placeholder={en.advancedFilter.creatorPlaceholder}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.provenance}
              </Typography>
              <TextField
                label={en.advancedFilter.provenance}
                value={filters.provenance}
                onChange={(e) => setFilters({ ...filters, provenance: e.target.value })}
                placeholder={en.advancedFilter.provenancePlaceholder}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.artworkDates}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={en.advancedFilter.dateFrom}
                  type="number"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: sanitizeNonNegative(e.target.value) })}
                  placeholder={en.advancedFilter.dateFromPlaceholder}
                  inputProps={numericInputProps}
                  error={rangeErrors.year !== null}
                  helperText={rangeErrors.year ?? undefined}
                  fullWidth
                />
                <TextField
                  label={en.advancedFilter.dateTo}
                  type="number"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: sanitizeNonNegative(e.target.value) })}
                  placeholder={en.advancedFilter.dateToPlaceholder}
                  inputProps={numericInputProps}
                  error={rangeErrors.year !== null}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {en.advancedFilter.dimensions}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl size="small" sx={{ width: 80 }}>
                  <InputLabel>{en.advancedFilter.unit}</InputLabel>
                  <Select
                    value={filters.dimensionUnit}
                    onChange={(e) => setFilters({ ...filters, dimensionUnit: e.target.value })}
                    label={en.advancedFilter.unit}
                  >
                    <MenuItem value="in">{en.advancedFilter.inches}</MenuItem>
                    <MenuItem value="cm">{en.advancedFilter.centimeters}</MenuItem>
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>{en.advancedFilter.height}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      value={filters.heightFrom}
                      onChange={(e) => setFilters({ ...filters, heightFrom: sanitizeNonNegative(e.target.value) })}
                      placeholder="0"
                      type="number"
                      inputProps={numericInputProps}
                      error={rangeErrors.height !== null}
                      helperText={rangeErrors.height ?? undefined}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="body2">{en.advancedFilter.to}</Typography>
                    <TextField
                      size="small"
                      value={filters.heightTo}
                      onChange={(e) => setFilters({ ...filters, heightTo: sanitizeNonNegative(e.target.value) })}
                      placeholder="100"
                      type="number"
                      inputProps={numericInputProps}
                      error={rangeErrors.height !== null}
                      sx={{ width: 80 }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>{en.advancedFilter.width}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      value={filters.widthFrom}
                      onChange={(e) => setFilters({ ...filters, widthFrom: sanitizeNonNegative(e.target.value) })}
                      placeholder="0"
                      type="number"
                      inputProps={numericInputProps}
                      error={rangeErrors.width !== null}
                      helperText={rangeErrors.width ?? undefined}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="body2">{en.advancedFilter.to}</Typography>
                    <TextField
                      size="small"
                      value={filters.widthTo}
                      onChange={(e) => setFilters({ ...filters, widthTo: sanitizeNonNegative(e.target.value) })}
                      placeholder="100"
                      type="number"
                      inputProps={numericInputProps}
                      error={rangeErrors.width !== null}
                      sx={{ width: 80 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
          <Button onClick={handleClear} color="secondary" disabled={loading}>
            {en.advancedFilter.clearAll}
          </Button>
          <Button onClick={handleClose} disabled={loading}>
            {en.advancedFilter.cancel}
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={loading || hasRangeError}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? en.activityTimeline.filtering : en.advancedFilter.applyFilters}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
